/**
 * Screen 12 (partial) — profile and the language switch.
 *
 * The language control is here and in the menu, and it changes EVERY screen.
 * Non-negotiable 1: one language per member. There is no per-screen
 * override, and choosing here writes it to the profile so the member's other
 * device agrees.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorNote, Shell } from "../components/Shell";
import { LOCALES, LOCALE_NAMES, setLocale, type Locale } from "../i18n";
import { api, clearTokens, isSignedIn, type Me } from "../lib/api";
import { formatDate } from "../lib/format";

export default function Profile({
  locale,
  onLocaleChange,
}: {
  locale: Locale;
  onLocaleChange: (next: Locale) => void;
}) {
  const { t } = useTranslation();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSignedIn()) return;
    api
      .me()
      .then(setMe)
      .catch(() => setError(t("error.generic")));
  }, [t]);

  async function choose(next: Locale) {
    await setLocale(next);
    onLocaleChange(next);
    // Persist to the profile when there is one, so the choice follows the
    // member rather than living only in this browser.
    if (isSignedIn()) {
      try {
        setMe(await api.updateMe({ locale: next }));
      } catch {
        /* the local choice already applied; the server can catch up later */
      }
    }
  }

  return (
    <Shell masthead area="profile" title={t("nav.profile")}>
      {error && <ErrorNote>{error}</ErrorNote>}

      {me && (
        <>
          <h2 className="screen__subtitle">{me.name}</h2>
          {me.member_no && (
            <p>
              {t("membership.member_no")}: <b>{me.member_no}</b>
            </p>
          )}
          {me.email && <p>{me.email}</p>}
          {me.valid_until && (
            <p>
              {t("membership.valid_until")}: {formatDate(me.valid_until, locale)}
            </p>
          )}
          {me.roles.length > 0 && <p className="fallback-note">{me.roles.join(" · ")}</p>}
        </>
      )}

      <h3 className="section">{t("nav.language")}</h3>
      {/* A segmented control, not three primary buttons: pink means "do
          something" everywhere else in the app, and here it was being used to
          mean "currently selected". */}
      <div className="segmented" role="group" aria-label={t("nav.language")}>
        {LOCALES.map((code) => (
          <button
            key={code}
            type="button"
            lang={code}
            className="segmented__option"
            aria-pressed={code === locale}
            onClick={() => void choose(code)}
          >
            {LOCALE_NAMES[code]}
          </button>
        ))}
      </div>

      {isSignedIn() ? (
        <button
          type="button"
          className="btn btn--quiet-danger"
          onClick={() => {
            // Best-effort: tell the server to retire every token, but clear
            // this device either way so a shared phone is not left signed in.
            api.logout().catch(() => undefined);
            clearTokens();
            window.location.href = "/";
          }}
        >
          {t("nav.logout")}
        </button>
      ) : (
        <Link className="btn btn--primary" to={`/signin?next=${encodeURIComponent("/profile")}`}>
          {t("auth.sign_in")}
        </Link>
      )}
    </Shell>
  );
}
